/**
 * Authentication Configuration for Microsoft 365 SSO
 * 
 * This module configures MSAL for authentication with Microsoft 365.
 * 
 * DEVELOPMENT MODE:
 * - Uses Microsoft Graph scopes (User.Read, offline_access)
 * - Suitable for early development without backend API registration
 * 
 * PRODUCTION MODE:
 * - Should use custom API scopes (api://{clientId}/access_as_user)
 * - Requires proper backend API registration in Azure AD
 * - Follow instructions in /docs/azure-ad-setup-guide.md for proper setup
 */

import { LogLevel, Configuration } from '@azure/msal-browser';

// Process env will be available after Next.js processes the .env file
// Use environment variable with fallback to development mode for local testing
const useMockAuth = process.env.REACT_APP_USE_MOCK_AUTH === 'true';

// MSAL configuration
export const msalConfig: Configuration = {
  auth: {
    // Use real client ID from env variable
    clientId: process.env.REACT_APP_CLIENT_ID || process.env.REACT_APP_MSAL_CLIENT_ID || 'development-client-id',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_TENANT_ID || 'common'}`,
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false, // Change to false to prevent double redirects
  },
  cache: {
    cacheLocation: 'sessionStorage', // Change to sessionStorage to avoid issues across tabs/windows
    storeAuthStateInCookie: true // Enable cookies for improved IE/Edge support and redirect handling
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        // Only log errors in production, disable all other logging
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          default:
            return;
        }
      },
      // Set log level to Error to minimize logging
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false
    }
  }
};

// Mock authentication configuration
export const developmentConfig = {
  // Use the environment variable to control mock auth
  enabled: useMockAuth,
  mockUser: {
    id: 1,
    email: 'developer@myaccounting.com',
    name: 'Developer User',
    role: 'admin' as const,
    created_at: new Date().toISOString()
  },
  // Token to use for mock authentication with backend
  developmentToken: 'development-token'
};

// Is this a development environment?
const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;

// Login request object with required scopes
export const loginRequest = {
  // IMPORTANT: Always use the API audience scope instead of Microsoft Graph
  // Format: api://{clientId}/access_as_user
  scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access_as_user`],
  // Fallback to Microsoft Graph scopes only if needed for testing
  // If the custom API scope doesn't work, uncomment this line and comment out the line above
  // scopes: ["User.Read", "profile", "email", "offline_access"],
};

// Protected resource mapping for API endpoints
export const protectedResources = {
  apiAccounting: {
    endpoint: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
    // IMPORTANT: Always use the API audience scope instead of Microsoft Graph
    scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access_as_user`],
    // Fallback option if the custom API scope doesn't work
    // If the custom API scope doesn't work, uncomment this line and comment out the line above
    // scopes: ["User.Read", "profile", "email"],
  }
};

// Authentication configuration initialized
